
import { useState } from "react";
import { Helmet } from "react-helmet";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { CheckCircle2, Circle, Download, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageTransition from "@/components/PageTransition";

interface ChecklistItem {
  id: string;
  category: string;
  task: string;
  description: string;
  priority: "High" | "Medium" | "Low";
  completed: boolean;
}

const SeoChecklist = () => {
  const [items, setItems] = useState<ChecklistItem[]>([
    // Technical SEO
    {
      id: "tech-1",
      category: "Technical SEO",
      task: "SSL Certification",
      description: "Ensure your website has a valid SSL certificate (HTTPS)",
      priority: "High",
      completed: false,
    },
    {
      id: "tech-2",
      category: "Technical SEO",
      task: "Mobile Responsiveness",
      description: "Test website responsiveness across different devices",
      priority: "High",
      completed: false,
    },
    {
      id: "tech-3",
      category: "Technical SEO",
      task: "Page Speed Optimization",
      description: "Optimize page loading speed (under 3 seconds)",
      priority: "High",
      completed: false,
    },
    {
      id: "tech-4",
      category: "Technical SEO",
      task: "Crawlability Check",
      description: "Ensure search engines can crawl your site properly",
      priority: "High",
      completed: false,
    },
    {
      id: "tech-5",
      category: "Technical SEO",
      task: "XML Sitemap",
      description: "Create and submit XML sitemap to search engines",
      priority: "Medium",
      completed: false,
    },
    {
      id: "tech-6",
      category: "Technical SEO",
      task: "Robots.txt",
      description: "Configure robots.txt file correctly",
      priority: "Medium",
      completed: false,
    },
    // On-Page SEO
    {
      id: "onpage-1",
      category: "On-Page SEO",
      task: "Title Tags Optimization",
      description: "Optimize title tags with primary keywords (under 60 characters)",
      priority: "High",
      completed: false,
    },
    {
      id: "onpage-2",
      category: "On-Page SEO",
      task: "Meta Descriptions",
      description: "Create compelling meta descriptions (under 160 characters)",
      priority: "Medium",
      completed: false,
    },
    {
      id: "onpage-3",
      category: "On-Page SEO",
      task: "Header Tags Structure",
      description: "Implement proper H1, H2, H3 tags structure",
      priority: "Medium",
      completed: false,
    },
    {
      id: "onpage-4",
      category: "On-Page SEO",
      task: "Image Optimization",
      description: "Optimize images with alt text and compression",
      priority: "Medium",
      completed: false,
    },
    {
      id: "onpage-5",
      category: "On-Page SEO",
      task: "URL Structure",
      description: "Create SEO-friendly URLs (short, descriptive, keyword-rich)",
      priority: "Medium",
      completed: false,
    },
    {
      id: "onpage-6",
      category: "On-Page SEO",
      task: "Internal Linking",
      description: "Implement strategic internal linking structure",
      priority: "Medium",
      completed: false,
    },
    // Content SEO
    {
      id: "content-1",
      category: "Content SEO",
      task: "Keyword Research",
      description: "Conduct thorough keyword research for target audience",
      priority: "High",
      completed: false,
    },
    {
      id: "content-2",
      category: "Content SEO",
      task: "Content Audit",
      description: "Audit existing content for quality and relevance",
      priority: "Medium",
      completed: false,
    },
    {
      id: "content-3",
      category: "Content SEO",
      task: "Content Calendar",
      description: "Create a content calendar for regular publishing",
      priority: "Medium",
      completed: false,
    },
    // Local SEO
    {
      id: "local-1",
      category: "Local SEO",
      task: "Google Business Profile",
      description: "Create and optimize Google Business Profile listing",
      priority: "High",
      completed: false,
    },
    {
      id: "local-2",
      category: "Local SEO",
      task: "Local Citations",
      description: "Build consistent NAP citations across directories",
      priority: "Medium",
      completed: false,
    },
    {
      id: "local-3",
      category: "Local SEO",
      task: "Review Management",
      description: "Implement strategy for gathering and managing reviews",
      priority: "Medium",
      completed: false,
    },
    // Off-Page SEO
    {
      id: "offpage-1",
      category: "Off-Page SEO",
      task: "Backlink Analysis",
      description: "Analyze current backlink profile quality",
      priority: "High",
      completed: false,
    },
    {
      id: "offpage-2",
      category: "Off-Page SEO",
      task: "Link Building Strategy",
      description: "Develop a strategy for acquiring quality backlinks",
      priority: "High",
      completed: false,
    },
  ]);

  const toggleItemCompletion = (id: string) => {
    setItems(
      items.map((item) =>
        item.id === id ? { ...item, completed: !item.completed } : item
      )
    );
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    // This would normally generate a PDF, but for simplicity we'll just show a toast
    toast.success("SEO Checklist downloaded successfully");
  };

  const categories = [...new Set(items.map((item) => item.category))];
  const completedCount = items.filter((item) => item.completed).length;
  const totalCount = items.length;
  const completionPercentage = Math.round((completedCount / totalCount) * 100);

  return (
    <PageTransition>
      <Helmet>
        <title>Complete SEO Checklist | Los Angeles SEO Group</title>
        <meta
          name="description"
          content="A comprehensive SEO checklist to ensure your website is fully optimized for search engines. Free resource from Los Angeles SEO Group."
        />
      </Helmet>

      <Navbar />

      <section className="py-12 md:py-20 bg-gradient-to-b from-muted/50 to-background">
        <div className="page-container">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Complete SEO <span className="text-gradient bg-sky-gradient">Checklist</span>
            </h1>
            <p className="text-lg text-foreground/70 mb-8">
              Use this comprehensive checklist to ensure your website is fully optimized for search engines.
              Track your progress and prioritize tasks to maximize your SEO efforts.
            </p>

            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
              <div className="bg-muted rounded-lg p-4 flex items-center gap-3">
                <div className="relative h-16 w-16">
                  <svg className="h-16 w-16" viewBox="0 0 36 36">
                    <circle
                      cx="18"
                      cy="18"
                      r="16"
                      fill="none"
                      stroke="#e2e8f0"
                      strokeWidth="2"
                    />
                    <circle
                      cx="18"
                      cy="18"
                      r="16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeDasharray={`${completionPercentage} 100`}
                      strokeDashoffset="25"
                      className="text-primary"
                      transform="rotate(-90 18 18)"
                    />
                  </svg>
                  <div className="absolute top-0 left-0 h-full w-full flex items-center justify-center">
                    <span className="text-xl font-bold">{completionPercentage}%</span>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-foreground/70">Completed</p>
                  <p className="font-medium">
                    {completedCount} of {totalCount} tasks
                  </p>
                </div>
              </div>

              <div className="flex gap-2">
                <Button variant="outline" onClick={handlePrint} className="flex gap-2">
                  <Printer className="h-4 w-4" />
                  <span>Print</span>
                </Button>
                <Button variant="default" onClick={handleDownload} className="flex gap-2">
                  <Download className="h-4 w-4" />
                  <span>Download PDF</span>
                </Button>
              </div>
            </div>

            {categories.map((category) => (
              <div key={category} className="mb-10">
                <h2 className="text-2xl font-bold mb-4">{category}</h2>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[50px]">Done</TableHead>
                      <TableHead>Task</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="w-[100px]">Priority</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items
                      .filter((item) => item.category === category)
                      .map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            <Checkbox
                              checked={item.completed}
                              onCheckedChange={() => toggleItemCompletion(item.id)}
                              id={item.id}
                            />
                          </TableCell>
                          <TableCell className="font-medium">
                            <label
                              htmlFor={item.id}
                              className={`cursor-pointer ${
                                item.completed ? "line-through text-foreground/50" : ""
                              }`}
                            >
                              {item.task}
                            </label>
                          </TableCell>
                          <TableCell
                            className={
                              item.completed ? "text-foreground/50" : "text-foreground/70"
                            }
                          >
                            {item.description}
                          </TableCell>
                          <TableCell>
                            <span
                              className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                                item.priority === "High"
                                  ? "bg-red-100 text-red-800"
                                  : item.priority === "Medium"
                                  ? "bg-yellow-100 text-yellow-800"
                                  : "bg-green-100 text-green-800"
                              }`}
                            >
                              {item.priority === "High" ? (
                                <CheckCircle2 className="h-3 w-3" />
                              ) : item.priority === "Medium" ? (
                                <CheckCircle2 className="h-3 w-3" />
                              ) : (
                                <Circle className="h-3 w-3" />
                              )}
                              {item.priority}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </div>
            ))}

            <div className="bg-muted p-6 rounded-lg mt-8">
              <h3 className="text-xl font-bold mb-2">Need Help Implementing This Checklist?</h3>
              <p className="mb-4">
                Our team of SEO experts can help you implement all these optimizations and more.
                Contact us today for a free consultation.
              </p>
              <Button asChild className="bg-desert-gradient hover:opacity-90">
                <a href="/contact">Get SEO Help</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </PageTransition>
  );
};

export default SeoChecklist;
